/**
 * The Menus_Backend's Generated_Client, as a composing application reaches it (R13.2).
 *
 * The Admin_Panel's menu-review and item-editor Bespoke_Pages compose this: both
 * applications' `AGENTS.md` named wiring the Bespoke_Page frontend to the Menus_Backend
 * review operations through a Generated_Client as the remaining step, and this is that
 * client. The operations module and the request/response types beneath
 * `src/client/generated/` are Orval's output; this barrel wraps them in a small,
 * correctly-typed façade so a consumer calls `createMenusReviewClient(configuration)`
 * and reaches the six operations by name, each resolving the payload the route sends.
 *
 * **The one correction this module makes** is the Orval result type. Orval types each
 * operation's result as its own `{ data, status, headers }` triple, which is what
 * Orval's own fetch client returns and not what `@hurdl/http-client`'s mutator resolves
 * with — the mutator reads the `{ data }` envelope and resolves the inner payload, or
 * `undefined` for a 204. The types cannot describe that read, so the cast is confined
 * to {@link readPayload} here, as the capabilities confine theirs to a `readData` at
 * the top of a store.
 *
 * The transport — the base address and the authenticated request function — is the
 * composing application's, built from {@link createMenusTransport} and passed on each
 * call. This client holds no address of its own, which is what lets the Admin_Panel
 * compose it without a `fetch` or address literal reaching its frontend sources.
 */

import {
  categoriseNewItem as categoriseOperation,
  correctMenuItemAllergens as correctAllergensOperation,
  createCategory as createCategoryOperation,
  editCategory as editCategoryOperation,
  editMenuItemDisplay as editDisplayOperation,
  listReviewQueue as listOperation,
  removeCategory as removeCategoryOperation,
  resolveReviewField as resolveFieldOperation,
  resolveReviewImage as resolveImageOperation,
  resolveSignificantChange as resolveOperation,
  setMenuItemNutrition as setNutritionOperation,
  uploadMenuItemImage as uploadImageOperation,
} from './generated/menus.js';
import { type Categories } from './generated/model/categories.js';
import { type CategoriseNewItemBody } from './generated/model/categoriseNewItemBody.js';
import { type Category } from './generated/model/category.js';
import { type CorrectMenuItemAllergensBody } from './generated/model/correctMenuItemAllergensBody.js';
import { type CreateCategoryBody } from './generated/model/createCategoryBody.js';
import { type EditCategoryBody } from './generated/model/editCategoryBody.js';
import { type EditMenuItemDisplayBody } from './generated/model/editMenuItemDisplayBody.js';
import { type ImageUpload } from './generated/model/imageUpload.js';
import { type MenuItems } from './generated/model/menuItems.js';
import { type RemoveCategoryBody } from './generated/model/removeCategoryBody.js';
import { type ResolveReviewFieldBody } from './generated/model/resolveReviewFieldBody.js';
import { type ResolveReviewImageBody } from './generated/model/resolveReviewImageBody.js';
import { type ResolveSignificantChangeBody } from './generated/model/resolveSignificantChangeBody.js';
import { type ReviewItemChangesItemField } from './generated/model/reviewItemChangesItemField.js';
import { type ReviewQueue } from './generated/model/reviewQueue.js';
import { type SetMenuItemNutritionBody } from './generated/model/setMenuItemNutritionBody.js';
import { type UploadMenuItemImageBody } from './generated/model/uploadMenuItemImageBody.js';
import {
  createMenusTransport,
  type MenusClientConfiguration,
} from './api/transport.js';

import {
  type RequestTransport,
  type TransportOptions,
} from '@hurdl/http-client';

export {
  createMenusTransport,
  type MenusClientConfiguration,
} from './api/transport.js';

// The request and response types a consumer composes against, re-exported so the
// Admin_Panel maps the client's shapes without reaching into the generated tree.
export { type ReviewItem } from './generated/model/reviewItem.js';
export { type ReviewItemReason } from './generated/model/reviewItemReason.js';
export { type ReviewItemExtracted } from './generated/model/reviewItemExtracted.js';
export { type ReviewItemExtractedAllergens } from './generated/model/reviewItemExtractedAllergens.js';
export { type ReviewItemExtractedNutrition } from './generated/model/reviewItemExtractedNutrition.js';
export { type ReviewItemChangesItem } from './generated/model/reviewItemChangesItem.js';
export { type ReviewItemChangesItemField } from './generated/model/reviewItemChangesItemField.js';
export { type ReviewItemChangesItemHeld } from './generated/model/reviewItemChangesItemHeld.js';
export { type ReviewItemChangesItemAcquired } from './generated/model/reviewItemChangesItemAcquired.js';
export { type ReviewItemChangesItemResolution } from './generated/model/reviewItemChangesItemResolution.js';
export { type ReviewItemImageChange } from './generated/model/reviewItemImageChange.js';
export { type CategoriseNewItemBody } from './generated/model/categoriseNewItemBody.js';
export { type ResolveSignificantChangeBody } from './generated/model/resolveSignificantChangeBody.js';
export { type ResolveSignificantChangeBodyDecision } from './generated/model/resolveSignificantChangeBodyDecision.js';
export { type ResolveReviewFieldBody } from './generated/model/resolveReviewFieldBody.js';
export { type ResolveReviewImageBody } from './generated/model/resolveReviewImageBody.js';
export { type EditMenuItemDisplayBody } from './generated/model/editMenuItemDisplayBody.js';
export { type CorrectMenuItemAllergensBody } from './generated/model/correctMenuItemAllergensBody.js';
export { type CorrectMenuItemAllergensBodyCorrectionsItem } from './generated/model/correctMenuItemAllergensBodyCorrectionsItem.js';
export { type SetMenuItemNutritionBody } from './generated/model/setMenuItemNutritionBody.js';
export { type MenuItem } from './generated/model/menuItem.js';
export { type MenuItems } from './generated/model/menuItems.js';
export { type MenuItemAllergens } from './generated/model/menuItemAllergens.js';
export { type MenuItemNutrition } from './generated/model/menuItemNutrition.js';
export { type Category } from './generated/model/category.js';
export { type Categories } from './generated/model/categories.js';
export { type CreateCategoryBody } from './generated/model/createCategoryBody.js';
export { type EditCategoryBody } from './generated/model/editCategoryBody.js';
export { type RemoveCategoryBody } from './generated/model/removeCategoryBody.js';
export { type UploadMenuItemImageBody } from './generated/model/uploadMenuItemImageBody.js';
export { type UploadMenuItemImageBodyMediaType } from './generated/model/uploadMenuItemImageBodyMediaType.js';
export { type ImageUpload } from './generated/model/imageUpload.js';

/**
 * The Menus_Backend review, edit, listing, category and image operations, each
 * resolving the payload the route sends (R13.2).
 *
 * The reads resolve their payload; the 204 writes resolve `void`; the create resolves
 * the created Category; the image upload resolves the stored public URL. Each throws
 * `ApiRequestError` from `@hurdl/errors` on a status at or above 400 — a 401 where the
 * actor is unresolved, 403 where authorisation refuses, 404 for an absent item or
 * category, 400 for a malformed body — so the Bespoke_Page composing this surfaces a
 * refusal rather than silently proceeding.
 */
export interface MenusReviewClient {
  /**
   * `GET /review-queue` — the whole queue.
   *
   * The route accepts a `restaurant` query filter, which this client does not yet
   * surface: the Admin_Panel surveys the one fast-food Product and reads the whole
   * queue, splitting it into new items and significant changes itself. Adding the
   * filter is an additive change to this operation when a second Restaurant needs it.
   */
  listReviewQueue(): Promise<ReviewQueue>;

  /**
   * `GET /menu-items?restaurant=` — the published items of one Restaurant.
   *
   * The reviewed data, which the reviewed-items view reads per Restaurant.
   * `includeRemoved` carries the removed items too, for a view that shows what has
   * gone; absent, the active items alone. The query is carried on the path the
   * transport issues, the generated operation declaring no query parameter.
   */
  listMenuItems(
    restaurant: string,
    options?: { readonly includeRemoved?: boolean },
  ): Promise<MenuItems>;

  /** `GET /categories?restaurant=` — the managed Categories of one Restaurant. */
  listCategories(restaurant: string): Promise<Categories>;

  /** `POST /categories` — create a managed Category; resolves the created Category. */
  createCategory(body: CreateCategoryBody): Promise<Category>;

  /** `POST /categories/:categoryKey` — rename a Category or set its icon. */
  editCategory(categoryKey: string, body: EditCategoryBody): Promise<void>;

  /** `POST /categories/:categoryKey/remove` — remove a managed Category. */
  removeCategory(categoryKey: string, body: RemoveCategoryBody): Promise<void>;

  /** `POST /review-queue/:itemKey/categorise`. */
  categoriseNewItem(
    itemKey: string,
    body: CategoriseNewItemBody,
  ): Promise<void>;

  /** `POST /review-queue/:itemKey/resolve`. */
  resolveSignificantChange(
    itemKey: string,
    body: ResolveSignificantChangeBody,
  ): Promise<void>;

  /** `POST /review-queue/:itemKey/fields/:field` — accept or reject one changed field. */
  resolveReviewField(
    itemKey: string,
    field: ReviewItemChangesItemField,
    body: ResolveReviewFieldBody,
  ): Promise<void>;

  /** `POST /review-queue/:itemKey/image` — accept or reject the proposed image. */
  resolveReviewImage(
    itemKey: string,
    body: ResolveReviewImageBody,
  ): Promise<void>;

  /** `POST /menu-items/:itemKey/display`. */
  editMenuItemDisplay(
    itemKey: string,
    body: EditMenuItemDisplayBody,
  ): Promise<void>;

  /** `POST /menu-items/:itemKey/allergens`. */
  correctMenuItemAllergens(
    itemKey: string,
    body: CorrectMenuItemAllergensBody,
  ): Promise<void>;

  /** `POST /menu-items/:itemKey/nutrition`. */
  setMenuItemNutrition(
    itemKey: string,
    body: SetMenuItemNutritionBody,
  ): Promise<void>;

  /**
   * `POST /menu-items/:itemKey/image` — upload an item's image; resolves the stored
   * public URL, which the route has already written onto the item.
   */
  uploadMenuItemImage(
    itemKey: string,
    body: UploadMenuItemImageBody,
  ): Promise<ImageUpload>;
}

/**
 * The one place the Orval result type is crossed.
 *
 * The mutator resolves the payload the route sent — the inner value of the `{ data }`
 * envelope, or `undefined` for a 204 — while Orval types the operation as returning its
 * `{ data, status, headers }` triple. This asserts the operation's resolved value as the
 * payload, which is what the transport actually resolves with; the cast is confined to
 * this function, as every consumer confines its own.
 */
async function readPayload<TPayload>(
  operation: Promise<unknown>,
): Promise<TPayload> {
  return (await operation) as TPayload;
}

/**
 * The write operations' seam: a 204 resolves no payload, so this awaits the operation
 * and returns, discarding the Orval triple the transport did not resolve. A separate
 * helper from {@link readPayload} because an explicit `void` type argument is a lint
 * error and reads worse than one function per intent.
 */
async function readVoid(operation: Promise<unknown>): Promise<void> {
  await operation;
}

/**
 * Builds the Menus_Backend review client against a transport the application supplies.
 *
 * @param configuration Where the review router is mounted, and the authenticated
 *   request function to issue through — the Admin_Panel's to decide.
 */
export function createMenusReviewClient(
  configuration: MenusClientConfiguration,
): MenusReviewClient {
  const transport: RequestTransport = createMenusTransport(configuration);

  // The options every generated operation carries: the transport, read by the mutator
  // and stripped before the request reaches `fetch`.
  const options = { transport } as unknown as TransportOptions;

  // The two filtered reads carry their query on the path, because the generated
  // operation declares no query parameter and the mutator joins the base to a fixed
  // path. The transport resolves the payload directly — the `{ data }` envelope read —
  // so these call it rather than the generated operation, with the method stated.
  const getQuery = <TPayload>(path: string): Promise<TPayload> =>
    transport<TPayload>(path, { method: 'GET' });

  return {
    listReviewQueue: () => readPayload<ReviewQueue>(listOperation(options)),
    listMenuItems: (restaurant, listOptions = {}) =>
      getQuery<MenuItems>(
        `/menu-items?restaurant=${encodeURIComponent(restaurant)}${
          listOptions.includeRemoved === true ? '&includeRemoved=true' : ''
        }`,
      ),
    listCategories: (restaurant) =>
      getQuery<Categories>(
        `/categories?restaurant=${encodeURIComponent(restaurant)}`,
      ),
    createCategory: (body) =>
      readPayload<Category>(createCategoryOperation(body, options)),
    editCategory: (categoryKey, body) =>
      readVoid(editCategoryOperation(categoryKey, body, options)),
    removeCategory: (categoryKey, body) =>
      readVoid(removeCategoryOperation(categoryKey, body, options)),
    categoriseNewItem: (itemKey, body) =>
      readVoid(categoriseOperation(itemKey, body, options)),
    resolveSignificantChange: (itemKey, body) =>
      readVoid(resolveOperation(itemKey, body, options)),
    resolveReviewField: (itemKey, field, body) =>
      readVoid(resolveFieldOperation(itemKey, field, body, options)),
    resolveReviewImage: (itemKey, body) =>
      readVoid(resolveImageOperation(itemKey, body, options)),
    editMenuItemDisplay: (itemKey, body) =>
      readVoid(editDisplayOperation(itemKey, body, options)),
    correctMenuItemAllergens: (itemKey, body) =>
      readVoid(correctAllergensOperation(itemKey, body, options)),
    setMenuItemNutrition: (itemKey, body) =>
      readVoid(setNutritionOperation(itemKey, body, options)),
    uploadMenuItemImage: (itemKey, body) =>
      readPayload<ImageUpload>(uploadImageOperation(itemKey, body, options)),
  };
}
